import { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppSidebar } from '@/components/AppSidebar';
import { ForceLogoutBanner } from '@/components/ForceLogoutBanner';
import { UsageAlertBanner } from '@/components/UsageAlertBanner';
import { NotificationsPanel } from '@/components/notifications/NotificationsPanel';
import { Button } from '@/components/ui/button';
import { Plus, Search } from 'lucide-react';
import { APP_CONFIG } from '@/config/app';

interface LayoutProps {
  children: ReactNode;
}

const PAGE_TITLES: Record<string, string> = {
  dashboard: 'Dashboard',
  communications: 'Communications',
  leads: 'Leads',
  jobs: 'Jobs',
  projects: 'Projects',
  estimates: 'Estimates',
  invoices: 'Invoices',
  customers: 'Customers',
  reports: 'Reports',
  settings: 'Settings',
  'system-health': 'System Health',
  profile: 'Profile',
  users: 'Users',
};

function pageTitle(pathname: string): string {
  const segment = pathname.split('/').filter(Boolean)[0] ?? '';
  return PAGE_TITLES[segment] ?? (segment ? segment.charAt(0).toUpperCase() + segment.slice(1) : 'Dashboard');
}

export function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="flex h-screen w-full overflow-hidden">
      <AppSidebar />

      <div className="flex min-w-0 flex-1 flex-col">
        <ForceLogoutBanner />
        <UsageAlertBanner />
        <header className="flex h-[60px] shrink-0 items-center gap-3 border-b bg-card px-6">
          <div className="min-w-0 text-base font-bold tracking-tight">
            {APP_CONFIG.name}
            <span className="font-semibold text-muted-foreground"> · {pageTitle(location.pathname)}</span>
          </div>

          <div className="ml-auto flex items-center gap-2.5">
            <div className="relative hidden h-9 w-80 items-center md:flex">
              <Search className="pointer-events-none absolute left-3 h-4 w-4 text-muted-foreground" />
              <input
                type="search"
                placeholder="Search…"
                className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-12 text-[13px] outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-[3px] focus:ring-primary/10"
              />
              <kbd className="pointer-events-none absolute right-3 rounded border bg-muted px-1 py-0.5 text-[10px] font-medium text-muted-foreground">
                ⌘K
              </kbd>
            </div>

            <NotificationsPanel />

            <Button className="h-9 gap-1.5 text-[13px] font-semibold" onClick={() => navigate('/leads/new')}>
              <Plus className="h-4 w-4" />
              New lead
            </Button>
          </div>
        </header>

        <main className="min-h-0 flex-1 overflow-y-auto bg-background">
          <div className="space-y-[22px] px-7 py-[26px]">{children}</div>
        </main>
      </div>
    </div>
  );
}
